import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import DarkModeToggle from './DarkModeToggle';
import api from '../lib/api';
import { 
  LayoutDashboard, 
  Home, 
  Users, 
  FileText, 
  DollarSign, 
  TrendingUp,
  LogOut,
  Building2,
  Menu,
  X,
  Map,
  FileSignature,
  Calendar,
  UserCheck,
  ClipboardCheck,
  ClipboardList,
  MessageSquareText,
  Landmark,
  Inbox,
  Megaphone,
  Wallet,
  Receipt,
  Settings,
  Coins,
  BarChart3,
} from 'lucide-react';

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [branding, setBranding] = useState<{ logoUrl?: string | null; faviconUrl?: string | null; sizes?: { systemLogoHeight?: number } }>({});

  // Atualiza permissões frescas + branding (logo/favicon) a cada carregamento.
  useEffect(() => {
    refreshUser();
    api
      .get('/branding')
      .then(({ data }) => {
        setBranding(data || {});
        if (data?.faviconUrl) {
          let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = data.faviconUrl;
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, dataTour: '' },
    { name: 'Mapa', href: '/map', icon: Map, dataTour: 'map-link' },
    { name: 'Imóveis', href: '/properties', icon: Home, dataTour: 'properties-link' },
    { name: 'Pessoas', href: '/people', icon: Users, dataTour: '' },
    { name: 'Contratos', href: '/leases', icon: FileText, dataTour: 'leases-link' },
    { name: 'Templates', href: '/contract-templates', icon: FileSignature, dataTour: '' },
    { name: 'Cobranças', href: '/charges', icon: DollarSign, dataTour: 'charges-link' },
    { name: 'Financeiro', href: '/finance', icon: TrendingUp, dataTour: '' },
    { name: 'Painel Financeiro', href: '/painel-financeiro', icon: BarChart3, dataTour: '' },
    { name: 'Funcionários', href: '/employees', icon: UserCheck, dataTour: 'employees-link' },
    { name: 'Departamentos', href: '/departments', icon: Building2, dataTour: '' },
    { name: 'Folha de Pagamento', href: '/payroll', icon: Calendar, dataTour: 'payroll-link' },
    { name: 'Compliance Cadastros', href: '/onboarding-compliance', icon: ClipboardCheck, dataTour: '' },
    { name: 'Angariação', href: '/property-capture', icon: ClipboardList, dataTour: '' },
    { name: 'CRM', href: '/crm', icon: MessageSquareText, dataTour: '' },
    { name: 'Atendimento', href: '/inbox', icon: Inbox, dataTour: '' },
    { name: 'Anúncios', href: '/listings', icon: Megaphone, dataTour: '' },
    { name: 'Caixa', href: '/caixa', icon: Wallet, dataTour: '' },
    { name: 'Contas a Pagar/Receber', href: '/contas', icon: Coins, dataTour: '' },
    { name: 'Notas Promissórias', href: '/promissory', icon: FileSignature, dataTour: '' },
    { name: 'Recibos', href: '/receipt-templates', icon: Receipt, dataTour: '' },
    { name: 'Configurações', href: '/settings', icon: Settings, dataTour: '' },
    { name: 'Banking', href: '/banking', icon: Landmark, dataTour: '' },
    { name: 'BankConf', href: '/bankconf', icon: Landmark, dataTour: '' },
  ].filter((item) => {
    if (user?.profile !== 'CAPTADOR') return true;
    if (item.href === '/banking') return false;
    if (item.href === '/bankconf') return false;
    if (item.href === '/charges') return false;
    if (item.href === '/finance') return false;
    return true;
  }).filter((item) => {
    if (item.href !== '/bankconf') return true;
    return ['ADMIN', 'DIRETOR'].includes(user?.profile || '');
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg-primary transition-colors">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-30 h-full w-64 bg-white dark:bg-dark-bg-secondary border-r border-gray-200 dark:border-dark-border
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between gap-2 px-3 py-4 border-b border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-center flex-1 min-w-0">
              {branding.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt="Logo"
                  style={{ maxHeight: branding.sizes?.systemLogoHeight || 72 }}
                  className="w-full max-w-[220px] object-contain"
                />
              ) : (
                <>
                  <Building2 className="w-8 h-8 text-primary-600 dark:text-dark-accent" />
                  <span className="text-xl font-bold dark:text-dark-text-primary">MacroImob</span>
                </>
              )}
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden flex-shrink-0 dark:text-dark-text-primary"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  data-tour={item.dataTour}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-primary-50 text-primary-700 font-medium dark:bg-dark-bg-tertiary dark:text-dark-accent' 
                      : 'text-gray-700 hover:bg-gray-50 dark:text-dark-text-primary dark:hover:bg-dark-bg-tertiary'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User info */}
          <div className="border-t border-gray-200 dark:border-dark-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-600 dark:bg-dark-accent rounded-full flex items-center justify-center text-white dark:text-gray-900 font-medium">
                  {user?.name?.charAt(0) || 'A'}
                </div>
                <div>
                  <p className="text-sm font-medium dark:text-dark-text-primary">{user?.name || 'Administrador'}</p>
                  <p className="text-xs text-gray-500 dark:text-dark-text-secondary">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-dark-text-secondary dark:hover:text-dark-text-primary rounded-lg hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary"
                title="Sair"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-10 h-16 bg-white dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border flex items-center px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden mr-4 dark:text-dark-text-primary"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h2 className="text-lg font-semibold dark:text-dark-text-primary">
              {navigation.find(n => n.href === location.pathname)?.name || 'Dashboard'}
            </h2>
          </div>
          <div data-tour="dark-mode-toggle">
            <DarkModeToggle />
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

